import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/cn'

const textAreaVariants = cva(
  'flex max-h-[500px] min-h-28 w-full justify-between rounded-lg border border-system-border-primary bg-background px-3 py-2 text-sm font-normal text-system-text-secondary ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      isError: {
        true: 'border-system-state-error',
        false: '',
      },
    },
    defaultVariants: {
      isError: false,
    },
  },
)

export type TextareaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    limitCharacters?: number
    value?: string
  } & VariantProps<typeof textAreaVariants>

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, limitCharacters, value = '', onChange, isError, ...props },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!props.disabled && onChange) {
        onChange(e)
      }
    }

    return (
      <div className="relative w-full">
        <textarea
          className={cn(textAreaVariants({ className, isError }))}
          ref={ref}
          maxLength={limitCharacters}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {limitCharacters && (
          <div className="mt-1 text-right text-sm text-muted-foreground">
            {value?.length ?? 0}/{limitCharacters}
          </div>
        )}
      </div>
    )
  },
)
Textarea.displayName = 'Textarea'

export { Textarea }
